/**
 * Everything the Quick Bytes grid shows instead of tiles —
 * ROUTE_INVENTORY §2.12 #6, #7 and #8.
 *
 * Extracted from `QuickBytesGrid` for the reason `GraphStates` was extracted
 * from the Knowledge Archive: the grid decides which of these to render, and
 * the states themselves are pure. Separating them means the empty copy can be
 * reviewed without standing up an infinite query.
 *
 * ## The skeleton is not a generic shimmer
 *
 * `QuickBytesGridSkeleton` reproduces the first twelve tiles of the LIVE band
 * pattern, `aspect-square` and all (§2.12 #7). A uniform grid of equal boxes
 * would be simpler and would make every tile jump when the real sizes land —
 * which on a bento layout means the whole grid reflows, not just one cell.
 *
 * ## Two empty states, and the difference is what to do next
 *
 * Filtered-empty is a dead end the user built, so it comes with the way out.
 * Unfiltered-empty means there is nothing to filter yet, and the only useful
 * action is somewhere else entirely — generate a lesson. Offering "Clear
 * filters" there would be a control that cannot help, on the screen where the
 * user is least able to tell the difference.
 */

import { Bookmark, Layers3, Loader2 } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { cn } from '@/lib/utils';
import { sizeForIndex } from '@/lib/mindspace/bandPattern';
import type { QuickBytesTileSize } from '@/lib/mindspace/bandPattern';

/** Grid-span classes per size. Kept in step with `QuickBytesTile`'s own map. */
export const TILE_SPAN: Record<QuickBytesTileSize, string> = {
  regular: 'col-span-1 row-span-1 aspect-square',
  tall: 'col-span-1 row-span-1 aspect-square md:col-span-1 md:row-span-2 md:aspect-auto',
  large: 'col-span-1 row-span-1 aspect-square md:col-span-2 md:row-span-2',
};

/** How many placeholder tiles the skeleton draws. Roughly one viewport. */
const SKELETON_TILES = 12;

/**
 * §2.12 #7 — the first page is in flight.
 *
 * Sizes come from `sizeForIndex`, not from a hand-written list, so the skeleton
 * cannot drift from the pattern it is imitating. The previous version inlined a
 * twelve-entry array that had already fallen out of step with the band.
 */
export function QuickBytesGridSkeleton() {
  // The phone's tile is a poster with a caption under it, so its skeleton is
  // too - nothing shifts when the feed lands.
  const phone = useMediaQuery('(max-width: 767.98px)');
  if (phone) {
    return (
      <div className='min-h-0 flex-1 overflow-hidden p-4'>
        <div
          className='grid grid-cols-2 gap-3'
          role='status'
          aria-label='Loading highlights'
        >
          {Array.from({ length: SKELETON_TILES }, (_, i) => (
            <div
              key={i}
              className='overflow-hidden rounded-2xl border border-border bg-card'
            >
              <div className='aspect-square animate-pulse bg-muted' />
              <div className='space-y-2 px-3 pb-3 pt-2.5'>
                <div className='h-3.5 w-3/4 animate-pulse rounded bg-muted' />
                <div className='h-3 w-1/2 animate-pulse rounded bg-muted' />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className='min-h-0 flex-1 overflow-hidden p-4'>
      <div
        className='grid grid-flow-row-dense grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4'
        role='status'
        aria-label='Loading highlights'
      >
        {Array.from({ length: SKELETON_TILES }, (_, i) => (
          <div
            key={i}
            className={cn(
              TILE_SPAN[sizeForIndex(i)],
              'animate-pulse rounded-xl border border-border/60 bg-white/40 dark:bg-white/[0.06]'
            )}
          />
        ))}
      </div>
    </div>
  );
}

/** §2.12 #8 — nothing matches, or nothing exists yet. */
export function QuickBytesEmptyState({
  hasFilters,
  onClear,
  onGenerate,
  bookmarksOnly = false,
  onShowAll,
}: {
  hasFilters: boolean;
  onClear: () => void;
  onGenerate: () => void;
  /** The Bookmarks view with no filters on: nothing bookmarked yet. It used
   *  to read "No highlights match your filters" - Bookmarks is a view, not a
   *  filter, and "Clear filters" was the wrong way out. */
  bookmarksOnly?: boolean;
  onShowAll?: () => void;
}) {
  if (bookmarksOnly) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-3 p-8 text-center'>
        <Bookmark className='h-10 w-10 text-muted-foreground/40' aria-hidden />
        <h3 className='text-base font-semibold text-foreground'>
          No bookmarks yet
        </h3>
        <p className='max-w-xs text-sm text-muted-foreground'>
          Tap the bookmark on any card to keep it here.
        </p>
        <Button variant='outline' onClick={onShowAll} className='max-md:h-11'>
          Browse all bytes
        </Button>
      </div>
    );
  }
  if (hasFilters) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-3 p-8 text-center'>
        <Layers3 className='h-10 w-10 text-muted-foreground/40' aria-hidden />
        <h3 className='text-base font-semibold text-foreground'>
          No highlights match your filters
        </h3>
        <p className='max-w-xs text-sm text-muted-foreground'>
          Try widening your filters - remove a tag, a source lesson, or the time
          window.
        </p>
        <Button variant='outline' onClick={onClear} className='max-md:h-11'>
          Clear filters
        </Button>
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col items-center justify-center gap-3 p-8 text-center'>
      <h3 className='text-base font-semibold text-foreground'>
        No highlights yet
      </h3>
      <p className='max-w-xs text-sm text-muted-foreground'>
        Highlights appear here once you generate a lesson with the highlights
        step enabled.
      </p>
      <Button onClick={onGenerate} className='mt-1 max-md:h-11'>
        Generate a lesson
      </Button>
    </div>
  );
}

/**
 * §2.12 #6 — the strip under the grid.
 *
 * Three states, and the third is silence: while more pages exist but none is in
 * flight, this renders nothing at all. That is right — the sentinel above it is
 * about to fetch, and a permanent "scroll for more" would be noise on a surface
 * whose whole premise is that it keeps going.
 *
 * The end state carries `loaded of total` rather than a bare "that's
 * everything", because on a filtered feed the count is the only confirmation
 * that the filter did what the user meant.
 */
export function QuickBytesFeedFooter({
  isFetchingNextPage,
  hasNextPage,
  loaded,
  total,
}: {
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  loaded: number;
  total: number;
}) {
  if (isFetchingNextPage) {
    return (
      <span className='inline-flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' aria-hidden />
        Loading more highlights…
      </span>
    );
  }

  if (!hasNextPage && loaded > 0) {
    return (
      <span className='text-xs text-muted-foreground'>
        You&apos;ve reached the end · {loaded} of {total}
      </span>
    );
  }

  return null;
}
